"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function WellnessInsightsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="157165" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/30 shadow-sm">
            <div className="aspect-[4/3] w-full animate-pulse bg-muted/40" />
            <CardHeader className="pb-4 pt-6">
              <div className="mb-3 h-5 w-24 animate-pulse rounded-md bg-muted/40" />
              <div className="h-6 w-full animate-pulse rounded-md bg-muted/40" />
              <div className="mt-2 h-6 w-2/3 animate-pulse rounded-md bg-muted/40" />
            </CardHeader>
            <CardFooter className="mt-auto pb-6 pt-0">
              <div className="h-4 w-32 animate-pulse rounded-md bg-muted/40" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 p-12 text-center">
        <p className="mb-4 text-muted-foreground font-medium">Unable to retrieve the latest wellness articles.</p>
        <Button variant="outline" onClick={refetch} className="rounded-full border-primary text-primary hover:bg-primary/5">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 p-12 text-center">
        <p className="text-muted-foreground font-medium">New medical insights are being prepared. Check back soon!</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border/50 bg-card shadow-sm transition-all duration-500 hover:shadow-xl hover:shadow-primary/5">
          <Link to={post.link || "#"} className="block aspect-[4/3] w-full overflow-hidden bg-muted">
            <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592322/img/wellness_lifestyle_placeholder.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          </Link>
          <CardHeader className="flex-grow pb-4 pt-6">
            {post.categories && post.categories.length > 0 && <Badge variant="secondary" className="mb-4 w-fit bg-accent text-accent-foreground font-medium tracking-wide">
                {post.categories[0].name} • 5 min read
              </Badge>}
            <Link to={post.link || "#"} className="block group-hover:text-primary">
              <CardTitle className="line-clamp-3 text-xl font-bold leading-tight text-secondary transition-colors duration-300">
                {post.title}
              </CardTitle>
            </Link>
          </CardHeader>
          <CardFooter className="mt-auto pb-6 pt-0">
            <Link to={post.link || "#"} className="group/link inline-flex items-center text-sm font-bold uppercase tracking-wider text-primary transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(0,150,255,0.4)]">
              Read full guide
              <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function WellnessInsights() {
  const wp_query_params = {
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-wellness-journal" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:40px_40px]"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="mb-4 text-3xl font-bold tracking-tighter text-secondary md:text-5xl uppercase">
            The Wellness Journal
          </h2>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Explore expert-curated medical research, personal health tips, and lifestyle advice from okfun to elevate your daily well-being.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <WellnessInsightsConsumer />
        </PostsProvider>

        <div className="mt-12 flex justify-center md:mt-16">
          <Link to="/WellnessGuidesPage">
            <Button size="lg" variant="outline" className="rounded-full px-10 border-primary text-primary font-bold uppercase tracking-widest shadow-sm transition-all duration-300 hover:bg-primary hover:text-white hover:scale-105 hover:shadow-primary/25">
              View All Guides
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}